<template>
  <div class="pnl" role="radiogroup" :aria-label="label || null" data-test="pnl">
    <label
      v-for="(n, i) in numbers"
      :key="n.phoneNumber"
      class="pnl-row"
      :class="{ 'is-selected': value === n.phoneNumber, 'is-disabled': disabled }"
    >
      <input
        type="radio"
        class="pnl-radio"
        :name="groupName"
        :value="n.phoneNumber"
        :checked="value === n.phoneNumber"
        :disabled="disabled"
        data-test="pnl-option"
        @change="$emit('input', n.phoneNumber)"
      >
      <span class="pnl-number">{{ format(n.phoneNumber) }}</span>
      <span v-if="place(n)" class="pnl-place">{{ place(n) }}</span>
      <span v-if="topBadge && i === 0" class="pnl-badge" data-test="pnl-badge">{{ topBadge }}</span>
    </label>
  </div>
</template>

<script>
/**
 * A list of phone numbers to pick ONE from — shared by the A2P wizard's texting-number step and the
 * WhatsApp wizard's number step (2026-09-11). It used to exist only inside A2PWizardPhoneNumbers.vue.
 *
 * Presentational only: the parent owns the numbers, the search and the purchase, because the two
 * screens buy through DIFFERENT paths (A2P attaches its messaging setup; WhatsApp must not). This
 * component never learns which.
 *
 * ONE number, by design — a practice has exactly one Teamio number (SMS capability is derived from
 * the single GENERAL_PHONE setting and both purchase paths refuse a second), hence radio buttons.
 *
 * NO PRICE (Saumil, 2026-09-11): Teamio includes the number, the practice is never charged for it,
 * so no row shows a figure. See phoneNumbers.js.
 *
 * v-model: `value` is the selected E.164 number ('' for none); `input` emits the picked one.
 */
import { formatUsNumber, numberPlace } from './phoneNumbers';

let groupSeq = 0;

export default {
  name: 'PhoneNumberList',

  props: {
    /** `[{ phoneNumber, locality, region }]` — anything else on the objects is ignored. */
    numbers: { type: Array, default: () => [] },
    /** The selected E.164 number, or ''. */
    value: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    /** Read out by screen readers for the group. */
    label: { type: String, default: '' },
    /** A badge on the FIRST row only — e.g. "Recommended". Empty = none. */
    topBadge: { type: String, default: '' },
  },

  data() {
    groupSeq += 1;
    return { groupName: `pnl-group-${groupSeq}` };
  },

  methods: {
    format: formatUsNumber,
    place: numberPlace,
  },
};
</script>

<style scoped>
.pnl {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  max-width: 640px;
}

.pnl-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem 0.75rem;
  margin: 0;
  padding: 0.6rem 0.85rem;
  border: 1px solid #c8cfcd;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  font-weight: 400;
}

.pnl-row:hover:not(.is-disabled) {
  border-color: #0f7259;
}

.pnl-row.is-selected {
  border-color: #0f7259;
  background: #e9f6f1;
  box-shadow: 0 0 0 1px #0f7259 inset;
}

.pnl-row.is-disabled {
  cursor: default;
  opacity: 0.6;
}

.pnl-radio {
  margin: 0;
  accent-color: #0f7259;
}

.pnl-number {
  font-size: 1rem;
  font-weight: 700;
  color: #0c0f0e;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.pnl-place {
  font-size: 0.85rem;
  color: #717977;
}

.pnl-badge {
  margin-left: auto;
  padding: 0.12rem 0.55rem;
  border-radius: 20px;
  background: #0f7259;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}
</style>
